
import React, { useState, useEffect } from "react";
import { Check, Search, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface RegionSelectProps {
  value: string;
  onChange: (value: string) => void;
}

export interface Region {
  id: number;
  name: string;
  display_name?: string;
  code?: string;
}

const RegionSelect = ({ value, onChange }: RegionSelectProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const { toast } = useToast();
  
  // Set up the selected region from value if available
  useEffect(() => {
    if (value && regions.length > 0) {
      const region = regions.find(r => r.id.toString() === value);
      if (region) {
        setSelectedRegion(region);
        setSearchValue(region.display_name || region.name);
      }
    }
  }, [value, regions]);
  
  // Fetch regions based on search term
  useEffect(() => {
    const fetchRegions = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No auth token found");
          setError("Authentication required");
          return;
        }
        
        console.log(`Searching regions with query: ${debouncedSearchValue || "all"}`);
        
        // Build the URL with query parameter if there's a search term
        const url = new URL("https://api.discoveryconciergeservices.com/api/v1/regions/search/");
        if (debouncedSearchValue) {
          url.searchParams.append("q", debouncedSearchValue);
        }
        
        const response = await fetch(url.toString(), {
          headers: {
            "Authorization": `Token ${token}`,
            "Accept": "application/json"
          }
        });
        
        if (!response.ok) {
          console.error(`Regions search failed: ${response.status} ${response.statusText}`);
          throw new Error(`Failed to fetch regions: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log("Regions search results:", data);
        
        if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
          setRegions(data.results);
          setOpen(true);
        } else {
          console.warn("No regions found in search results");
          setRegions([]);
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
        setError(error instanceof Error ? error.message : "Failed to load regions");
        
        toast({
          title: "Error loading regions",
          description: "Unable to load region data. Please try a different search or contact support.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (debouncedSearchValue.trim() === "") {
      setRegions([]);
      setOpen(false);
    } else {
      fetchRegions();
    }
  }, [debouncedSearchValue, toast]);
  
  const handleSelectRegion = (region: Region) => {
    setSelectedRegion(region);
    setSearchValue(region.display_name || region.name);
    onChange(region.id.toString());
    setOpen(false);
  };
  
  return (
    <div className="space-y-2 md:col-span-2">
      <label htmlFor="licensePlateRegion" className="block text-sm font-medium text-black">
        License Plate State/Region
      </label>
      
      <div className="relative w-full">
        <Input
          id="licensePlateRegion"
          type="text"
          placeholder="Search for state/region..."
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            if (e.target.value.trim() === "") {
              setSelectedRegion(null);
              onChange(""); // Clear the selection when search is cleared
            }
          }}
          className={`w-full bg-white border-gray-200 text-black pr-10 ${error ? 'border-red-500' : ''}`}
          onFocus={() => {
            if (debouncedSearchValue.trim() !== "" && regions.length > 0) setOpen(true);
          }}
        />
        {/* Display either the check mark (when selected) or search icon */}
        {selectedRegion ? (
          <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
        ) : (
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        )}
        
        {open && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200">
            {isLoading ? (
              <div className="p-2">
                <Skeleton className="h-8 w-full mb-2" />
                <Skeleton className="h-8 w-full mb-2" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <div className="max-h-60 overflow-auto">
                {regions.length === 0 ? (
                  <div className="py-3 px-4 text-sm text-gray-500">No regions found</div>
                ) : (
                  <ul>
                    {regions.map((region) => (
                      <li
                        key={region.id}
                        onClick={() => handleSelectRegion(region)}
                        className={cn(
                          "py-2 px-4 text-sm cursor-pointer hover:bg-gray-100 flex items-center text-black",
                          selectedRegion?.id === region.id ? "bg-gray-100" : ""
                        )}
                      >
                        <span>{region.display_name || region.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {isLoading && !open && <p className="text-sm text-gray-500">Loading regions...</p>}
      
      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
      
      {regions.length === 0 && !isLoading && !error && searchValue.trim() !== "" && (
        <p className="text-sm text-amber-600 mt-1">
          No regions found. Try a different search term.
        </p>
      )}
      
      {/* Removed the selected region text display since we now show checkmark in the input */}
    </div>
  );
};

export default RegionSelect;
